/* START CUSTOMIZATION */
import { useState, useEffect } from 'react';
import { Message } from 'semantic-ui-react';
import { defineMessages, useIntl } from 'react-intl';
import { isInternalURL, withBlockExtensions } from '@plone/volto/helpers';
// eslint-disable-next-line
import teaserTemplate from '@kitconcept/volto-slider-block/icons/teaser-template.svg';

import { Listing } from '@designsystem/stories/04-organisms/Listing';
import { normalizeContent } from '@fhnw/helpers/DesignSystem/normalizeContent';
import { transformToCard } from '@fhnw/helpers/DesignSystem/cardTransformers';
import { getListingImageSizes } from '@fhnw/helpers/DesignSystem/imageSizes.ts';
/* END CUSTOMIZATION */

const messages = defineMessages({
  PleaseChooseContent: {
    id: 'Please choose an existing content as source for this element',
    defaultMessage:
      'Please choose an existing content as source for this element',
  },
  ButtonText: {
    id: 'Continue reading',
    defaultMessage: 'Continue reading',
  },
});

const SliderView = (props) => {
  const { data, isEditMode = false, slideIndex, setSlideIndex } = props;
  const intl = useIntl();
  /* START CUSTOMIZATION */
  // In edit mode slides without a target must stay in the list: the sidebar
  // ObjectListWidget and this view share the slide index, and dropping items
  // here made freshly added slides impossible to select (#1536).
  const slides = (data.slides || []).filter(
    (item) => item !== null && (isEditMode || item?.href?.[0]),
  );

  const [selectedIndex, setSelectedIndex] = useState(0);

  const handleChange = (index) => {
    setSelectedIndex(index);
    if (isEditMode) {
      setSlideIndex(index);
    }
  };

  useEffect(() => {
    // This syncs the current slide with the objectwidget (or other sources
    // able to access the slider context)
    // that can modify the SliderContext (and come here via props slideIndex)
    if (isEditMode) {
      setSelectedIndex(slideIndex);
    }
  }, [slideIndex, setSelectedIndex, isEditMode]);

  return (
    <>
      {(slides?.length === 0 || !slides) && isEditMode && (
        <Message>
          <div className="teaser-item default">
            <img src={teaserTemplate} alt="" />
            <p>{intl.formatMessage(messages.PleaseChooseContent)}</p>
          </div>
        </Message>
      )}
      {slides?.length > 0 && (
        <Listing
          type="product"
          slider={true}
          currentPage={selectedIndex}
          list={true}
          onChange={handleChange}
          items={slides.map((item, index) => {
            const content = item?.href?.[0];
            const card = transformToCard(
              content,
              intl,
              'product',
              undefined,
              undefined,
              false,
              getListingImageSizes('product', 'slider', false),
            );

            if (item?.title) {
              card.title = item.title;
            } else if (!content) {
              // Placeholder card for a slide whose target is not chosen yet
              card.title = intl.formatMessage(messages.PleaseChooseContent);
            }

            if (item?.description) {
              card.description = item.description;
            }

            if (item?.preview_image?.[0] || content?.hasPreviewImage) {
              card.image = normalizeContent(
                {
                  format: 'landscape',
                  image: item?.preview_image?.[0] || content,
                },
                false,
                getListingImageSizes('product', 'slider', false),
              );
            }

            if (card?.image) {
              card.image.aspect = 'landscape';
            }

            if (item?.head_title) {
              card.category = item.head_title;
            }

            // External targets open in a new tab. The readmore Button needs
            // the explicit target: it always forwards its (possibly empty)
            // target prop, which overrides UniversalLink's external-link
            // default of _blank (#1536).
            const isExternal = card.link && !isInternalURL(card.link);
            if (isExternal) {
              card.target = '_blank';
            }

            if (!item.hideButton && content) {
              card.readmore = {
                href: card.link,
                ...(isExternal && { target: '_blank' }),
                icon: 'arrow_right_triangle',
                icon_after: true,
                label:
                  item.buttonText || intl.formatMessage(messages.ButtonText),
              };
            } else if (card?.readmore) {
              delete card.readmore;
            }

            return card;
          })}
        />
      )}
    </>
  );
  /* END CUSTOMIZATION */
};

export default withBlockExtensions(SliderView);
